// SidebarComponent.jsx
import { useState, useEffect } from "react";
import {
  Menu,
  Sidebar,
  MenuItem,
  SubMenu,
  useProSidebar,
} from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";

import { useSidebarContext } from "../../../context/SidebarContext";
import { useTeam } from "../../../context/TeamContext";

import { useTheme, Box, Typography, IconButton } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import SwitchRightOutlinedIcon from "@mui/icons-material/SwitchRightOutlined";
import SwitchLeftOutlinedIcon from "@mui/icons-material/SwitchLeftOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";

import { tokens } from "../../../theme";
import "./styles.css";

// Updated Item Component
const Item = ({ title, to, icon, activePath, subItems, label }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const getIconRoute = (name) => {
    switch (name) {
      case "Frontend":
        return <LanguageOutlinedIcon />;
      case "Backend":
        return <ComputerOutlinedIcon />;
      case "CS":
        return <SupportAgentOutlinedIcon />;
      case "QA":
        return <BugReportOutlinedIcon />;
      case "Automation":
        return <AutorenewOutlinedIcon />;
      case "Mobile":
        return <PhoneAndroidOutlinedIcon />;
      case "Settings":
        return <SettingsOutlinedIcon />;
      default:
        return <PeopleIcon />;
    }
  };

  if (subItems && subItems.length > 0) {
    // Determine if any of the subitems' path matches activePath
    const isSubMenuOpen = subItems.some(
      (subItem) => `${to}/${subItem.id}` === activePath
    );

    return (
      <SubMenu
        label={label}
        className="custom-menu"
        icon={icon}
        defaultOpen={isSubMenuOpen}
      >
        {subItems.map((menu) => {
          const linkDetail = `${to}/${menu.id}`;
          return (
            <MenuItem
              key={menu.id} // Use unique ID as key
              active={activePath === linkDetail}
              style={{ color: colors.grey[100] }}
              icon={getIconRoute(menu.name)}
              routerLink={<Link to={linkDetail} />}
            >
              <Typography>{menu.name}</Typography>
            </MenuItem>
          );
        })}
      </SubMenu>
    );
  }

  return (
    <MenuItem
      active={activePath === to}
      style={{ color: colors.grey[100] }}
      icon={icon}
      routerLink={<Link to={to} />}
    >
      <Typography>{title}</Typography>
    </MenuItem>
  );
};

const SidebarComponent = () => {
  const { teams } = useTeam();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { sidebarRTL, setSidebarRTL, sidebarImage } = useSidebarContext();
  const { collapseSidebar, toggleSidebar, collapsed, broken } = useProSidebar();
  const location = useLocation();

  const [activePath, setActivePath] = useState("");

  useEffect(() => {
    const path = location.pathname;

    // If at root '/', set activePath to '/project'
    if (path === "/") {
      setActivePath("/project");
    } else {
      setActivePath(path);
    }
  }, [location]);

  return (
    <Box
      sx={{
        position: "sticky",
        display: "flex",
        height: "100vh",
        top: 0,
        bottom: 0,
        zIndex: 10000,
        "& .sidebar": {
          border: "none",
        },
        "& .menu-icon": {
          backgroundColor: "transparent !important",
        },
        "& .menu-item": {
          backgroundColor: "transparent !important",
        },
        "& .menu-anchor": {
          color: "inherit !important",
          backgroundColor: "transparent !important",
        },
        "& .menu-item:hover": {
          color: `${colors.blueAccent[500]} !important`,
          backgroundColor: "transparent !important",
        },
        "& .menu-item.open:hover": {
          color: "inherit !important",
        },
        "& .menu-item.active": {
          color: `${colors.greenAccent[500]} !important`,
          backgroundColor: "transparent !important",
        },
      }}
    >
      <Sidebar
        breakPoint="md"
        rtl={sidebarRTL}
        backgroundColor={colors.primary[400]}
        image={sidebarImage}
      >
        <Menu iconshape="square">
          <MenuItem
            icon={
              collapsed ? (
                <MenuOutlinedIcon onClick={() => collapseSidebar()} />
              ) : sidebarRTL ? (
                <SwitchLeftOutlinedIcon
                  onClick={() => setSidebarRTL(!sidebarRTL)}
                />
              ) : (
                <SwitchRightOutlinedIcon
                  onClick={() => setSidebarRTL(!sidebarRTL)}
                />
              )
            }
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!collapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography variant="h3" color={colors.grey[100]}>
                  Everfit Team
                </Typography>
                <IconButton onClick={broken ? toggleSidebar : collapseSidebar}>
                  <CloseOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>
          <Box paddingLeft={collapsed ? undefined : "10%"}>
            <Item
              title="Project"
              to="/project"
              icon={<AccountTreeIcon />}
              activePath={activePath}
            />

            <Item
              title="Work Plan"
              to="/plan"
              icon={<GroupsIcon />}
              activePath={activePath}
              label="Plans"
            />
            <Item
              title="Member"
              to="/member"
              icon={<PersonIcon />}
              activePath={activePath}
            />
            <Item
              title="Manage Team"
              to="/team"
              icon={<Diversity3OutlinedIcon />}
              activePath={activePath}
              subItems={teams}
              label="Teams"
            />
            {/* <Item
              title="Contacts Information"
              to="/contacts"
              icon={<ContactsOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            /> */}
            {/* <Item
              title="Invoices Balances"
              to="/invoices"
              icon={<ReceiptOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            /> */}
            {/* <Item
              title="Profile Form"
              to="/form"
              icon={<PersonOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            /> */}
            {/* <Item
              title="Calendar"
              to="/calendar"
              icon={<CalendarTodayOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            /> */}
            {/* <Item
              title="FAQ Page"
              to="/faq"
              icon={<HelpOutlineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            /> */}

            {/* <Typography
              variant="h6"
              color={colors.grey[300]}
              sx={{ m: "15px 20px 5px 20px" }}
            >
              Charts
            </Typography>
            <Item
              title="Bar Chart"
              to="/bar"
              icon={<BarChartOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Pie Chart"
              to="/pie"
              icon={<PieChartOutlineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Line Chart"
              to="/line"
              icon={<TimelineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Geography Chart"
              to="/geography"
              icon={<MapOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            /> */}
          </Box>
        </Menu>
      </Sidebar>
    </Box>
  );
};

export default SidebarComponent;
